import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Box, ButtonBase, Typography } from "@mui/material";
import { HowToVote } from "@mui/icons-material";
import { motion, useReducedMotion } from "framer-motion";
import { useHistory } from "react-router-dom";
import { colors } from "../../theme/colors";
import { isLobbyV2Path, toLobbyPath } from "../../universal/v2Routes";
import { remainingSecondsFromExpiresAt } from "../../utils/matchAcceptance";

const MotionBox = motion.create(Box);

export function shouldShowPersistentMapVote(mapVoteState, pathname) {
    return Boolean(mapVoteState?.matchId) && !isLobbyV2Path(pathname);
}

export default function PersistentMapVoteStatus({ mapVoteState = null }) {
    const history = useHistory();
    const reduceMotion = useReducedMotion();
    const [nowMs, setNowMs] = useState(() => Date.now());

    useEffect(() => {
        const tick = () => setNowMs(Date.now());
        tick();
        const intervalId = window.setInterval(tick, 250);
        return () => window.clearInterval(intervalId);
    }, [mapVoteState?.matchId, mapVoteState?.expiresAt]);

    const remainingSeconds = remainingSecondsFromExpiresAt(
        mapVoteState?.expiresAt,
        nowMs,
    );
    const timerLabel =
        remainingSeconds == null ? "--s" : `${remainingSeconds}s`;
    const hasVoted = Boolean(mapVoteState?.myVote);
    const resolved = Boolean(mapVoteState?.resolvedMap);

    return (
        <MotionBox
            initial={reduceMotion ? false : { opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -4 }}
            transition={{
                duration: reduceMotion ? 0 : 0.22,
                ease: [0.22, 1, 0.36, 1],
            }}
            sx={{ minWidth: 0 }}
        >
            <ButtonBase
                type="button"
                aria-label="Map vote in progress. Return to lobby to pick a map."
                onClick={() =>
                    history.push(toLobbyPath(history.location?.pathname))
                }
                sx={{
                    minWidth: { xs: "11.25rem", sm: "14.25rem", lg: "15rem" },
                    height: { xs: "3.55rem", sm: "4rem", xl: "4.15rem" },
                    px: { xs: "0.9rem", sm: "1.15rem" },
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: { xs: "0.72rem", sm: "0.9rem" },
                    borderRadius: "14px",
                    position: "relative",
                    border: "1px solid rgba(238,174,67,0.34)",
                    background:
                        "linear-gradient(180deg, rgba(29,26,22,0.985), rgba(11,11,14,0.99))",
                    backdropFilter: "blur(18px)",
                    WebkitBackdropFilter: "blur(18px)",
                    boxShadow:
                        "inset 0 1px 0 rgba(255,232,188,0.09), 0 0 0 1px rgba(232,163,61,0.045), 0 16px 38px rgba(0,0,0,0.58), 0 5px 20px rgba(232,163,61,0.1)",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: "1.1rem",
                        right: "1.1rem",
                        height: "1px",
                        background:
                            "linear-gradient(90deg, transparent, rgba(255,205,116,0.72), transparent)",
                        pointerEvents: "none",
                    },
                    color: colors.text.primary,
                    cursor: "pointer",
                    overflow: "hidden",
                    transition:
                        "transform 200ms cubic-bezier(0.22,1,0.36,1), border-color 200ms ease, background 200ms ease, box-shadow 200ms ease",
                    "&:hover": {
                        transform: "translateY(-1px)",
                        borderColor: "rgba(242,183,82,0.5)",
                        background:
                            "linear-gradient(180deg, rgba(36,30,22,0.99), rgba(13,12,12,0.99))",
                        boxShadow:
                            "inset 0 1px 0 rgba(255,232,188,0.12), 0 0 0 1px rgba(232,163,61,0.07), 0 19px 44px rgba(0,0,0,0.64), 0 6px 24px rgba(232,163,61,0.14)",
                    },
                    "&.Mui-focusVisible": {
                        outline: `2px solid ${colors.border.focus}`,
                        outlineOffset: "2px",
                    },
                    "@media (prefers-reduced-motion: reduce)": {
                        transition: "none",
                        "&:hover": { transform: "none" },
                    },
                }}
            >
                <Box
                    aria-hidden
                    sx={{
                        width: 32,
                        height: 32,
                        display: "grid",
                        placeItems: "center",
                        color: colors.accent.soft,
                        flexShrink: 0,
                    }}
                >
                    <HowToVote sx={{ fontSize: "1.35rem" }} />
                </Box>

                <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
                    <Typography
                        component="span"
                        sx={{
                            color: colors.accent.soft,
                            fontSize: { xs: "0.61rem", sm: "0.68rem" },
                            fontWeight: 750,
                            lineHeight: 1,
                            letterSpacing: "0.13em",
                        }}
                    >
                        {resolved ? "MAP CHOSEN" : "MAP VOTE"}
                    </Typography>
                    <Typography
                        component="span"
                        sx={{
                            mt: "0.22rem",
                            color: colors.text.primary,
                            fontFamily: "'Orbitron', sans-serif",
                            fontSize: { xs: "0.94rem", sm: "1.06rem" },
                            fontWeight: 650,
                            lineHeight: 1,
                            letterSpacing: "0.035em",
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        {timerLabel}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: { xs: "none", sm: "block" },
                        width: "1px",
                        height: "1.85rem",
                        ml: "auto",
                        backgroundColor: colors.border.default,
                    }}
                />
                <Typography
                    component="span"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        color: colors.text.secondary,
                        fontSize: "0.69rem",
                        fontWeight: 700,
                        lineHeight: 1,
                        letterSpacing: "0.09em",
                        whiteSpace: "nowrap",
                    }}
                >
                    {resolved ? "LOBBY" : hasVoted ? "VOTED" : "VOTE"}
                </Typography>
            </ButtonBase>
        </MotionBox>
    );
}

PersistentMapVoteStatus.propTypes = {
    mapVoteState: PropTypes.object,
};
